import React, { useState } from 'react';
import {
    CodeBracketIcon,
    ChartBarIcon,
    ClipboardDocumentIcon,
    CogIcon,
    SwatchIcon,
    DocumentTextIcon,
    RocketLaunchIcon,
    UserGroupIcon,
    CameraIcon,
} from '@heroicons/react/24/outline';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import { baseUrl } from '../config';



const ProducerBuddyAIIntegration = () => {
    const [activeSection, setActiveSection] = useState(null);

    const productionStages = [
        {
            title: "Pre-Production",
            icon: RocketLaunchIcon,
            sections: [
                {
                    title: "Plan Your Works",
                    icon: CogIcon,
                    details: [
                        "AI-assisted project management",
                        "Automated task scheduling",
                        "Resource allocation optimization",
                    ],
                },
                {
                    title: "Script Analysis",
                    icon: DocumentTextIcon,
                    details: [
                        "Sentiment and emotional arc analysis",
                        "Dialogue optimization",
                        "Narrative inconsistency detection",
                    ],
                },
                {
                    title: "Budgeting",
                    icon: ChartBarIcon,
                    details: [
                        "Intelligent cost forecasting",
                        "Budget allocation recommendations",
                        "Risk assessment and mitigation",
                    ],
                },
                {
                    title: "Casting Assistant",
                    icon: UserGroupIcon,
                    details: [
                        "AI-driven talent matching",
                        "Performance prediction models",
                        "Cast chemistry analysis",
                    ],
                },
                {
                    title: "Location",
                    icon: CameraIcon,
                    details: [
                        "Virtual location scouting",
                        "Logistical feasibility assessment",
                        "Permit and compliance checks",
                    ],
                },
            ],
        },
        {
            title: "Production",
            icon: CodeBracketIcon,
            sections: [
                {
                    title: "Progress Management",
                    icon: ChartBarIcon,
                    details: [
                        "Real-time production tracking",
                        "Predictive scheduling and alerts",
                        "Automated reporting and dashboards"
                    ]
                },
                {
                    title: "Cast Management",
                    icon: UserGroupIcon,
                    details: [
                        "Actor availability monitoring",
                        "Talent utilization optimization",
                        "On-set performance analysis"
                    ]
                },
                {
                    title: "Scheduling Assistant",
                    icon: CogIcon,
                    details: [
                        "AI-powered shot planning",
                        "Crew and equipment allocation",
                        "Workflow automation"
                    ]
                },
                {
                    title: "Documentation",
                    icon: ClipboardDocumentIcon,
                    details: [
                        "Automated script logging",
                        "Digital production notes",
                        "Searchable footage archives"
                    ]
                }
            ]
        },
        {
            title: "Post-Production",
            icon: SwatchIcon,
            sections: [
                {
                    title: "Editing",
                    icon: DocumentTextIcon,
                    details: [
                        "Intelligent scene pacing analysis",
                        "Automated video assembly",
                        "Editorial decision support"
                    ]
                },
                {
                    title: "Visual Effects",
                    icon: SwatchIcon,
                    details: [
                        "AI-generated visual concepts",
                        "Predictive VFX complexity modeling",
                        "Automated compositing and cleanup"
                    ]
                },
                {
                    title: "Marketing Analyst",
                    icon: ChartBarIcon,
                    details: [
                        "Audience behavior prediction",
                        "Trailer optimization recommendations",
                        "Personalized marketing campaigns"
                    ]
                },
                {
                    title: "Advertisements",
                    icon: DocumentTextIcon,
                    details: [
                        "Dynamic ad content generation",
                        "Targeted distribution analytics",
                        "Performance monitoring and refinement"
                    ]
                },
                {
                    title: "Posters, Trailers, Distributions",
                    icon: ClipboardDocumentIcon,
                    details: [
                        "AI-powered design and content creation",
                        "Automated versioning and localization",
                        "Intelligent distribution channel selection"
                    ]
                }
            ]
        }
    ];

    return (
        <>
        <SEO
          title="AI Integration for Film Production"
          description="Explore how ProducerBuddy integrates AI across every stage of film production — script analysis, budget forecasting, casting intelligence, and distribution strategy."
          url="https://producerbuddy.pilomation.com/AIintregationau"
        />
        <Nav/>
        

        <div className="min-h-screen bg-white py-10 relative overflow-hidden">
            <style>
                {`
                    @keyframes waterFlow {
                        0% {
                            transform: translateY(0) translateX(0) rotate(0deg);
                            opacity: 0.3;
                            }
                        25% {
                            transform: translateY(-20%) translateX(5%) rotate(-2deg);
                            opacity: 0.4;
                        }
                        50% {
                            transform: translateY(-50%) translateX(10%) rotate(2deg);
                            opacity: 0.5;
                        }
                        75% {
                            </nav>
                            transform: translateY(-75%) translateX(5%) rotate(-1deg);
                            opacity: 0.4;
                        }
                        100% {
                            transform: translateY(-100%) translateX(0) rotate(0deg);
                            opacity: 0.3;
                        }
                    }

                    @keyframes waterWave {
                        0%, 100% {
                            transform: scale(1);
                            opacity: 0.3;
                        }
                        50% {
                            transform: scale(1.02);
                            opacity: 0.4;
                        }
                    }

                    .animate-water-flow {
                        animation: waterFlow 12s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;
                        background: linear-gradient(180deg, rgba(0, 128, 128, 0.2) 0%, rgba(0, 128, 128, 0.1) 50%, rgba(0, 128, 128, 0.2) 100%);
                        background-size: 100% 300%;
                        will-change: transform, opacity;
                    }

                    .animate-water-flow-2 {
                        animation: waterFlow 12s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite reverse;
                        background: linear-gradient(180deg, rgba(0, 128, 128, 0.1) 0%, rgba(0, 128, 128, 0.2) 50%, rgba(0, 128, 128, 0.1) 100%);
                        background-size: 100% 300%;
                        will-change: transform, opacity;
                    }
                `}
            </style>
            {/* Animated teal background */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-teal-100">
                    <div className="absolute inset-0 bg-teal-200 opacity-30 animate-water-flow"></div>
                    <div className="absolute inset-0 bg-teal-200 opacity-30 animate-water-flow-2"></div>
                </div>
            </div>

            {/* Rest of the component remains the same */}
            {/* Header Section */}
            <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
                <h1 className="text-5xl font-extrabold mb-4 text-black">
                    Producer Buddy: AI-Powered Film Production
                </h1>
                <p className="text-xl text-black mb-10">
                    Revolutionizing Film Production with Advanced AI Tools
                </p>
            </div>

            {/* Main Content */}
            <div className="max-w-5xl mx-auto px-4 space-y-8 relative z-10">
                {productionStages.map((stage, stageIndex) => (
                    <div
                        key={stageIndex}
                        className="bg-white/90 backdrop-blur-sm rounded-lg shadow-lg hover:shadow-2xl transition-shadow border border-teal-100"
                    >
                        <div
                            className="flex items-center justify-between p-6 cursor-pointer hover:bg-teal-50 transition rounded-t-lg"
                            onClick={() => setActiveSection(activeSection === stageIndex ? null : stageIndex)}
                        >
                            <div className="flex items-center">
                                <stage.icon className="w-8 h-8 text-teal-600 mr-4" />
                                <h2 className="text-2xl font-bold text-gray-900">{stage.title}</h2>
                            </div>
                            <span className="text-teal-600 text-2xl font-bold">
                                {activeSection === stageIndex ? '−' : '+'}
                            </span>
                        </div>

                        {activeSection === stageIndex && (
                            <div className="p-6 space-y-6 border-t border-teal-100">
                                {stage.sections.map((section, sectionIndex) => (
                                    <div key={sectionIndex} className="bg-white/80 p-4 rounded-lg">
                                        <div className="flex items-center mb-3">
                                            <section.icon className="w-6 h-6 text-teal-600 mr-3" />
                                            <h3 className="text-xl font-semibold text-gray-900">
                                                {section.title}
                                            </h3>
                                        </div>
                                        <ul className="space-y-3">
                                            {section.details.map((detail, detailIndex) => (
                                                <li key={detailIndex} className="flex items-start text-gray-700">
                                                    <svg
                                                        className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                    <span className="text-gray-700">{detail}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Footer Section */}
            <div className="mt-16 text-center text-gray-600 relative z-10">
                Revolutionizing Film Production with AI | Producer Buddy © 
            </div>
        </div>
        </>
    );
};

export default ProducerBuddyAIIntegration;
